import './caseStudy.css'
import noShit from '../Images/noShit.webp'
import dtp from '../Images/dtap.png'
import wiref from '../Images/fwfw.png'
import scrns from '../Images/screens.png'
import ia from '../Images/IA.png'
import Footer from "./footer"
import RevealTitle from "./RevealTitle"
import { useState } from 'react';

export default function Prj1() {
    const [isOpen, setIsOpen] = useState(false);

    return (
        <>
            <div className="cs-wrap">
                {/* Hero */}
                <header className="cs-hero">
                    <p className="cs-eyebrow">Case study · Design &amp; Web</p>
                    <RevealTitle text="CasaVUE" className="cs-title" />
                    <p className="cs-subtitle">Making premium living effortless.</p>
                </header>

                <div className="cs-cover">
                    <img src="https://ik.imagekit.io/swalih/wrkTheme.png?updatedAt=1751375058206"
                        alt="CasaVUE theme" loading="lazy" />
                </div>

                {/* Meta */}
                <div className="cs-meta">
                    <div><p className="cs-meta-label">Project</p><p className="cs-meta-value">CasaVUE</p></div>
                    <div><p className="cs-meta-label">Role</p><p className="cs-meta-value">Product, UI, Experience &amp; Branding Designer</p></div>
                    <div><p className="cs-meta-label">Tasks</p><p className="cs-meta-value">User Research &amp; Testing, UI Design, Design Strategy &amp; Content</p></div>
                    <div><p className="cs-meta-label">Timeline</p><p className="cs-meta-value">3 weeks</p></div>
                </div>

                {/* Quote */}
                <div className="cs-quote">
                    <img className="cs-quote-img" src={noShit} alt="Luxury living" />
                    <p className="cs-quote-text">The charm of luxury living lies in its exclusivity.</p>
                </div>

                {/* Problem */}
                <section className="cs-section">
                    <h2 className="cs-label">Problem Statement</h2>
                    <p className="cs-statement">What sets this real-estate app apart from others?</p>
                    <p className="cs-body">Despite countless real-estate platforms offering all kinds of listings — from budget studios to suburban homes — there's still no solution dedicated solely to ultra-luxury villas. We're filling that gap.</p>
                </section>

                {/* Challenge */}
                <section className="cs-section">
                    <h2 className="cs-label">Challenge</h2>
                    <p className="cs-statement">It's not just about selling properties, but redefining exclusivity.</p>
                    <p className="cs-body">The challenge is to create a platform that not only connects buyers with ultra-luxury villas but also builds a sustainable, trusted ecosystem — one that delivers lasting value, profitability, and a seamless experience in a competitive real-estate market.</p>
                </section>

                {/* Research */}
                <section className="cs-section">
                    <h2 className="cs-label">Research</h2>
                    <p className="cs-body">Identifying the need for an exclusive ultra-luxury villa platform and the key drivers of its success: trust, elegance, and a seamless experience.</p>
                </section>

                {/* Why */}
                <section className="cs-section">
                    <h2 className="cs-label">Why this idea works</h2>
                    <p className="cs-body">In markets like the UAE, where demand for ultra-luxury villas is rising as more billionaires and global elites choose to live and invest, this app fills a clear gap. Unlike generic property platforms, it offers only 100% verified luxury listings, 24/7 personalized support, and advanced, user-friendly search — setting a new standard of trust and exclusivity in high-end real estate.</p>
                </section>

                {/* Data model */}
                <section className="cs-section">
                    <h2 className="cs-label">Data Provision Model</h2>
                    <div className="cs-figure"><img src={dtp} alt="Data provision model" style={{ cursor: 'auto' }} /></div>
                    <p className="cs-body" style={{ marginTop: 'clamp(20px,3vw,28px)' }}>Data is sourced from multiple decentralized real-estate providers, with only verified inputs and controlled access granted to the property-listing endpoint. This aggregation from diverse sources enables a rich, trustworthy showcase of luxury properties to the end user.</p>
                </section>

                {/* User interviews */}
                <section className="cs-section">
                    <h2 className="cs-label">User Interviews</h2>
                    <p className="cs-statement">User research bridges assumptions to insights.</p>
                    <p className="cs-body">While I haven't conducted formal interviews, I had insightful conversations with a cousin and a friend — both of whom work in real estate in areas like Palm Jumeirah. Their immediate reaction to a platform focused solely on ultra-luxury, never-before-owned villas was highly positive. They noted that high-net-worth buyers in Dubai often seek exclusive properties that are untouched, private, and come with a premium lifestyle experience. These early discussions gave enough confidence to explore the idea further.</p>
                    <p className="cs-item-head">Targeted Users</p>
                    <ul className="cs-list">
                        <li>High-net-worth individuals (HNWIs)</li>
                        <li>Real-estate investors focused on luxury housing</li>
                        <li>International buyers relocating to the UAE</li>
                        <li>Developers of newly built luxury properties</li>
                        <li>Concierge services representing ultra-luxury clients</li>
                    </ul>
                </section>

                {/* IA */}
                <section className="cs-section cs-section--wide">
                    <h2 className="cs-label">Information Architecture</h2>
                    <div className="cs-figure">
                        <img src={ia} alt="Information Architecture" onClick={() => setIsOpen(true)} />
                    </div>
                    <p className="cs-caption">Tap to zoom</p>
                </section>

                {/* Wireframes */}
                <section className="cs-section cs-section--wide">
                    <h2 className="cs-label">Wireframes</h2>
                    <div className="cs-figure"><img src={wiref} alt="Wireframes overview" style={{ cursor: 'auto' }} /></div>
                </section>
                <div className="cs-links">
                    <a className="cs-linkBtn" href="https://www.figma.com/design/lVMmogIQeS9NaLLXnBRRrV/Untitled?node-id=66-53&t=G1x534nx63UgrGkc-1" target="_blank" rel="noreferrer">Open Figma wireframe ↗</a>
                </div>

                {/* Pain points */}
                <section className="cs-section">
                    <h2 className="cs-label">Common Pain Points</h2>
                    <p className="cs-statement">Pain points reveal opportunities for meaningful innovation.</p>

                    <div className="cs-item"><p className="cs-item-head">No platform exclusively for brand-new luxury villas</p><p className="cs-item-body">Buyers looking for fresh, never-before-owned villas struggle to find a platform that filters out pre-owned properties entirely.</p></div>
                    <div className="cs-item"><p className="cs-item-head">Scattered listings across multiple portals</p><p className="cs-item-body">Luxury listings are fragmented across various websites, forcing users to jump between tabs and agents, wasting time and trust.</p></div>
                    <div className="cs-item"><p className="cs-item-head">Lack of verified &amp; high-quality listings</p><p className="cs-item-body">Many listings lack verification, up-to-date photos, or 3D tours, making it hard for serious buyers to confidently shortlist properties.</p></div>
                    <div className="cs-item"><p className="cs-item-head">Generic experience for premium clients</p><p className="cs-item-body">High-net-worth buyers expect a premium digital experience, but most platforms offer the same layout and UX used for budget rentals.</p></div>
                    <div className="cs-item"><p className="cs-item-head">No real-time developer availability updates</p><p className="cs-item-body">Buyers often face disappointment after contacting agents, only to find a property is already under offer, due to poor syncing with developers.</p></div>
                </section>

                {/* Prototype */}
                <section className="cs-section cs-section--wide">
                    <h2 className="cs-label">Prototype</h2>
                    <div className="cs-figure"><img src={scrns} alt="Prototype screenshots" style={{ cursor: 'auto' }} /></div>
                </section>
                <section className="cs-section">
                    <div className="cs-item"><p className="cs-item-head">One tap to luxury living</p><p className="cs-item-body">Skip the clutter. Browse only brand-new, premium villas — no resale listings, no noise. Every property is verified and fresh from the developer.</p></div>
                    <div className="cs-item"><p className="cs-item-head">Your private property concierge</p><p className="cs-item-body">A refined interface with a tailored search — choose Villa, Penthouse, or Townhouse and find handpicked listings in top-tier locations like Palm Jumeirah or Dubai Hills.</p></div>
                    <div className="cs-item"><p className="cs-item-head">Found something you love?</p><p className="cs-item-body">Enter your name and number to request a call (no agent spam), just expert help when you want it — plus instant brochures and a secure handoff to verified developers.</p></div>
                </section>

                {/* Key findings */}
                <section className="cs-section">
                    <h2 className="cs-label">Key Findings</h2>
                    <p className="cs-statement">Key findings unlock the door to user-driven design breakthroughs.</p>

                    <div className="cs-item"><p className="cs-item-head">Purchase intent &amp; usage</p></div>
                    <ul className="cs-list">
                        <li>40% of conversations revealed active interest in buying a brand-new luxury villa within the next 12–18 months.</li>
                        <li>The rest browse for inspiration or future investment, valuing visual experience, credibility, and premium filters.</li>
                    </ul>

                    <div className="cs-item"><p className="cs-item-head">Desired features</p></div>
                    <ul className="cs-list">
                        <li>Verified-only listings with zero resale history.</li>
                        <li>Virtual walk-throughs for every home.</li>
                        <li>Ultra-clear filters to avoid "non-luxury" distractions.</li>
                    </ul>

                    <div className="cs-item"><p className="cs-item-head">Monetization &amp; trust</p></div>
                    <ul className="cs-list">
                        <li>Buyers show zero tolerance for ads, even non-intrusive ones.</li>
                        <li>They prefer a clean, premium, invite-only feel over monetization gimmicks.</li>
                        <li>Users trust platforms that visually reflect wealth and discretion.</li>
                    </ul>
                </section>
            </div>

            {isOpen && (
                <div className="cs-modal" onClick={() => setIsOpen(false)}>
                    <img src={ia} alt="Zoomed information architecture" />
                </div>
            )}

            <Footer />
        </>
    )
}
